import { print } from "graphql";
import gql from "graphql-tag";
import { useQuery } from "react-query";
import { getFreshAccessToken } from "../use-user";

const LOOKUP_BY_SHIPPING_TRACKING = gql`
  query GetShippingLabelTracking($id: String!) {
    shippingses(filter: { tracking_number: { eq: $id } }) {
      items {
        id
        tracking_number
        takeback_id
        shipping_metadata_list {
          key
          value
        }
      }
    }
  }
`;

// Query to lookup takeback by ID
const LOOKUP_BY_ID = gql`
  query LookupById($id: String!) {
    takeback(id: $id) {
      created_at
      customer_id
      data
      deleted_at
      eventId
      id
      product_id
      quantity
      schema_version
      shipping_provider_id
      shipping_tracking_id
      source
      takeback_processing_history_list {
        parent_status
        processed_at
        id
        status
        updated_at
      }
      timestamp
      type_name
      updated_at
      customer_id_belongsTo {
        name
        last_name
        id
      }
      shippingss {
        items {
          tracking_number
        }
      }
    }
  }
`;

// Cache for in-flight requests to prevent race conditions
const inflightRequests = new Map();

export const useTakebackLookup = () => {
  const performGraphQLLookup = async ({ query, variables }) => {
    const accessToken = getFreshAccessToken();
    const res = await fetch(`${process.env.REACT_APP_TES_URL}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": process.env.REACT_APP_CLIENT_ID || "lego-test",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        query: print(query),
        variables,
      }),
    });

    if (!res.ok) {
      throw new Error(
        `GraphQL request failed: ${res.status} ${res.statusText}`
      );
    }

    const result = await res.json();

    if (result.errors) {
      throw new Error(
        `GraphQL errors: ${result.errors.map((e) => e.message).join(", ")}`
      );
    }

    return result;
  };

  // Single function that handles the lookup logic
  const lookupTakeback = async (identifier) => {
    const trimmedIdentifier = identifier?.trim();

    if (!trimmedIdentifier) {
      return null;
    }

    // Check if we already have an in-flight request for this identifier
    if (inflightRequests.has(trimmedIdentifier)) {
      return inflightRequests.get(trimmedIdentifier);
    }

    // Create the lookup promise
    const lookupPromise = (async () => {
      try {
        // Try looking up by shipping tracking ID
        let takebackId = trimmedIdentifier;
        let shippingsData = null;
        try {
          const shippingsResult = await performGraphQLLookup({
            query: LOOKUP_BY_SHIPPING_TRACKING,
            variables: { id: trimmedIdentifier },
          });
          shippingsData = shippingsResult?.data?.shippingses?.items?.[0];

          if (shippingsData?.takeback_id) {
            takebackId = shippingsData.takeback_id;
          }
        } catch (error) {
          console.warn("Shipping lookup failed:", error);
          // Continue with original identifier
        }

        // Check if this is a fake tracking ID in format <store>_<epoch>
        const isFakeTrackingId = /^(CO|AR)_\d+$/.test(takebackId);

        if (isFakeTrackingId) {
          return {
            ...shippingsData,
            trackingId: trimmedIdentifier,
            isFullXPackage: true,
            store: takebackId.substring(0, 2),
            fakeTakebackId: takebackId,
          };
        }

        // Finally, try looking up by takeback ID
        try {
          const idResult = await performGraphQLLookup({
            query: LOOKUP_BY_ID,
            variables: { id: takebackId },
          });
          const idData = idResult?.data?.takeback;

          if (idData) {
            return idData;
          }
        } catch (error) {
          console.warn("ID lookup failed:", error);
        }

        // No results found
        return null;
      } finally {
        // Clean up the in-flight request
        inflightRequests.delete(trimmedIdentifier);
      }
    })();

    // Store the promise in the cache
    inflightRequests.set(trimmedIdentifier, lookupPromise);

    return lookupPromise;
  };

  return lookupTakeback;
};

// Hook for reactive takeback lookup with React Query caching
export const useTakebackLookupQuery = (identifier, options = {}) => {
  const lookupTakeback = useTakebackLookup();

  return useQuery(
    ["takeback-lookup", identifier],
    () => lookupTakeback(identifier),
    {
      enabled: Boolean(identifier?.trim()),
      retry: 2,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};
